import { IClientDetailedV3, IDiagnosis } from '~/services/api/client/types';
import {
  IClientAddressRequest,
  IClientAllergyResponse,
  IClientDeEscalationResponse,
  IClientMedicationDeliveryResponse,
  IClientMedicationResponse,
  IClientPhone,
  IClientPhoneRequest,
  IClientSecondaryDiagnosisResponse,
  IClientSupportContanctResponse,
  IClientTeam,
  IClientTeamRequest,
  IClientTreatmentPlanRequest,
  IClientTreatmentPlanResponse,
  IClientTriggerResponse,
} from '~/services/api/clientDetails/types';
import { IShelter, IShelterRequest } from '~/services/api/shelters/types';
import { IAddressExtended } from '~/services/api/types';
import { ITeam } from '~/store/user/types';
import memberTypes from '~/ui/constants/memberTypes';
import { IInitialValues as IClientInitialValues } from '~/ui/pages/Clients/LocalAdmin/Form/initialValues';
import {
  IAllergyInitialValues,
  IDeEscalationInitialValues,
  IMedicationDeliveryInitialValues,
  IMedicationInitialValues,
  IPrimaryDiagnosisInitialValues,
  ISecondaryDiagnosisInitialValues,
  ITriggerInitialValues,
} from '~/ui/pages/Clients/reusable/HealthDetails/Forms/types';
import { IInitialValues as SupportContactInitialValues } from '~/ui/pages/Clients/reusable/SupportContacts/Form/types';
import adjustDatesToInitialDST from '~/ui/pages/MedicationLogistics/helpers/adjustDatesToInitialDST';
import { destructHours, setNoon } from './date/date';
import removeActPrefix from './text/removeActPrefix';
import replaceAt from './text/replaceAt';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const formatDefaultValues = <T>(currentUser: any): T => {
  const { address, teams } = currentUser;
  const { workDetails } = currentUser;

  const primaryRole = memberTypes.find(
    type =>
      type.label.toLowerCase() === removeActPrefix(currentUser.primaryRole?.name).toLowerCase(),
  )?.value;

  const formattedValues = {
    ...currentUser,
    ...(currentUser.status === 'Pending'
      ? {
          email: currentUser.email.split('@')[0],
          emailDomain: `@${currentUser.email.split('@')[1]}`,
        }
      : {}),
    subRoleIds: currentUser.subRoles.map((subRole: any) => subRole.id),
    address: {
      ...address,
      stateId: address.state.id,
    },
    teamIds: (teams || []).map((team: ITeam) => team.id),
    workDetails: {
      ...workDetails,
      credentialsId: workDetails.credentials.id,
      educationId: workDetails.education.id,
      employmentStatusId: workDetails.employmentStatus.id,
      hireDate: setNoon(workDetails.hireDate),
    },
    primaryRole,
  };
  if (currentUser.teams)
    return { ...formattedValues, teams: currentUser.teams.map((team: any) => team.id) };
  return formattedValues;
};

export const formatClientDefaultValues = (
  currentClient: IClientDetailedV3,
  clientTeam: IClientTeam,
): IClientInitialValues => {
  const primaryPhone =
    currentClient.phones.find(phone => phone.isPrimary) || currentClient.phones[0];

  return {
    ...currentClient,
    phone: primaryPhone.number,
    genderId: currentClient.gender.id,
    raceId: currentClient.race?.id,
    teamId: clientTeam?.team.id,
    admissionDetails: {
      referralSourceId: clientTeam?.admissionDetails.referralSource.id,
      admissionDate: setNoon(clientTeam?.admissionDetails.admissionDate),
    },
    dateOfBirth: setNoon(currentClient.dateOfBirth),
    address: {
      ...currentClient.address,
      stateId: currentClient.address.state.id,
    },
    medicaIdRecertificationDate: currentClient.medicaIdRecertificationDate
      ? setNoon(currentClient.medicaIdRecertificationDate)
      : null,
  };
};

export const formatTriggerDefaultValues = (
  currentTrigger: IClientTriggerResponse,
): ITriggerInitialValues => ({
  frequency: currentTrigger.frequency,
  hasAlerts: currentTrigger.hasAlerts,
  note: currentTrigger.note,
  notificationOn: currentTrigger.notificationOn,
  triggerDate: currentTrigger.triggerDate,
  triggerId: currentTrigger.trigger.id,
  teamMembersToNotify: currentTrigger.teamMembersToNotify.map(item => item.id),
  type: currentTrigger.type,
  time: currentTrigger.triggerDate ? new Date(currentTrigger.triggerDate) : null,
});

export const formatAddressDefaultValues = (
  currentAddress: IAddressExtended,
): IClientAddressRequest => ({
  address: {
    stateId: currentAddress.state.id,
    city: currentAddress.city,
    line1: currentAddress.line1,
    line2: currentAddress.line2,
    zipCode: currentAddress.zipCode,
    poBox: currentAddress.poBox,
    shelterName: currentAddress.shelterName,
  },
  typeId: currentAddress.type?.id,
  setAsPrimary: currentAddress.isPrimary,
  archivePrimary: true,
  shelterId: currentAddress.shelter?.id,
  isShelter: currentAddress.isShelterAddress,
});

export const formatPhoneDefaultValues = (currentPhone: IClientPhone): IClientPhoneRequest => ({
  number: currentPhone.number,
  typeId: currentPhone.type.id,
  setAsPrimary: currentPhone.isPrimary,
  archivePrimary: true,
});

export const formatTeamDefaultValues = (currentTeam: IClientTeam): IClientTeamRequest => ({
  setAsPrimary: currentTeam.isPrimary,
  referralSourceId: currentTeam.admissionDetails?.referralSource.id,
  admissionDate: currentTeam.admissionDetails?.admissionDate,
  teamId: currentTeam.team.id,
});

export const formatMedicationDefaultValues = (
  currentMedication: IClientMedicationResponse,
): IMedicationInitialValues => ({
  id: currentMedication.medication.id,
  note: currentMedication.note,
  dose: currentMedication.dose,
  injectable: currentMedication.injectable,
  outsideFacility: currentMedication.outsideFacility,
  startDate: currentMedication.startDate
    ? replaceAt(currentMedication.startDate, 11, '12')
    : new Date().toISOString(),
  endDate: currentMedication.endDate ? replaceAt(currentMedication.endDate, 11, '12') : undefined,
});

export const formatMedicationDeliveryDefaultValues = (
  currentMedicationDelivery: IClientMedicationDeliveryResponse,
  teamId: string,
): IMedicationDeliveryInitialValues => ({
  id: currentMedicationDelivery.id,
  note: currentMedicationDelivery.note,
  notDelivered: currentMedicationDelivery.notDelivered,
  nonDeliveryReasonId: currentMedicationDelivery.nonDeliveryReason?.id,
  date: currentMedicationDelivery.date,
  nextDueDate: currentMedicationDelivery.nextDueDate,
  teamMemberId: currentMedicationDelivery.teamMember?.id,
  lastInjection: currentMedicationDelivery?.lastInjection,
  outsideFacility: currentMedicationDelivery?.outsideFacility,
  archiveMedication: currentMedicationDelivery?.isArchived,
  hasAlerts: currentMedicationDelivery?.hasAlerts,
  medicationIds: currentMedicationDelivery?.medicationIds,
  clientId: currentMedicationDelivery.client?.id,
  statusChangeLog: currentMedicationDelivery?.statusChangeLog,
  notificationOn: currentMedicationDelivery?.notificationOn,
  groupDelivery: !!currentMedicationDelivery?.groupId,
  groupId: currentMedicationDelivery?.groupId,
  teamId: Number(teamId),
  dateRange:
    currentMedicationDelivery?.groupStartDate && currentMedicationDelivery?.groupEndDate
      ? [
          new Date(currentMedicationDelivery.groupStartDate),
          new Date(currentMedicationDelivery.groupEndDate),
        ]
      : null,
  notificationTime: currentMedicationDelivery?.notificationTime
    ? destructHours(currentMedicationDelivery?.notificationTime)
    : null,
  teamMembersToNotify: currentMedicationDelivery?.teamMembersToNotify?.map(tm => tm.id) || [],
  time:
    currentMedicationDelivery.date && currentMedicationDelivery.id
      ? adjustDatesToInitialDST(
          currentMedicationDelivery?.groupStartDate,
          currentMedicationDelivery?.date,
        )
      : null,
});

export const formatDeEsclationDefaultValues = (
  currentDeEscalation: IClientDeEscalationResponse,
): IDeEscalationInitialValues => ({
  id: currentDeEscalation.deEscalationTechnique.id,
  note: currentDeEscalation.note,
});

export const formatAllergyDefaultValues = (
  currentAllergy: IClientAllergyResponse,
): IAllergyInitialValues => ({
  id: currentAllergy.allergy.id,
  note: currentAllergy.note,
  severityLevelId: currentAllergy.severityLevel?.id,
});

export const formatSecondaryDiagnosisDefaultValues = (
  secondaryDiagnosis: IClientSecondaryDiagnosisResponse,
): ISecondaryDiagnosisInitialValues => ({
  id: secondaryDiagnosis.diagnosis.id,
  note: secondaryDiagnosis.note,
  statusId: secondaryDiagnosis.status?.id,
});

export const formatPrimaryDiagnosisDefaultValues = (
  primaryDiagnosis: IDiagnosis,
): IPrimaryDiagnosisInitialValues => ({
  id: primaryDiagnosis.diagnosis.id,
  note: primaryDiagnosis.note,
  statusId: primaryDiagnosis.status?.id,
});

export const formatSupportContactDefaultValues = (
  supportContact: IClientSupportContanctResponse,
): SupportContactInitialValues & { hasAddress: boolean } => {
  const { address } = supportContact;

  return {
    firstName: supportContact.firstName,
    lastName: supportContact.lastName,
    phone: supportContact.phone,
    email: supportContact.email,
    relationshipId: supportContact.relationship.id,
    address: address
      ? {
          stateId: (address.state || {}).id,
          city: address.city,
          zipCode: address.zipCode,
          line1: address.line1,
          line2: address.line2,
        }
      : null,
    hasAddress: !!address,
  };
};

export const formatTreatmentPlanDefaultValues = (
  currentTreatmentPlan: IClientTreatmentPlanResponse,
): IClientTreatmentPlanRequest => ({
  caseManagerId: currentTreatmentPlan.caseManager?.id,
  effectiveDate: setNoon(currentTreatmentPlan.effectiveDate),
  nextReviewDate: setNoon(currentTreatmentPlan.nextReviewDate),
  goals: currentTreatmentPlan.goals.filter(goal => !goal.isArchived),
  setActive: !currentTreatmentPlan.active,
  typeId: currentTreatmentPlan.type?.id || null,
});

export const formatShelterDefaultValues = (shelter: IShelter): IShelterRequest | undefined =>
  shelter
    ? {
        name: shelter.name,
        phone: shelter.phone,
        address: {
          stateId: shelter.address.state.id,
          city: shelter.address.city,
          zipCode: shelter.address.zipCode,
          line1: shelter.address.line1,
          line2: shelter.address.line2,
        },
      }
    : undefined;
